import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Clock, ArrowUpRight, MapPin, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { WhatsAppButton } from "@/components/ui-extras/WhatsApp";
import { usePageSEO } from "@/lib/seo";
import { Link } from "react-router-dom";

export default function ContactPage() {
  usePageSEO({
    title: "Contact | Stichting FIN",
    description:
      "Neem contact op met Stichting FIN in Rotterdam. Voor algemene vragen, samenwerking of doorverwijzing. Zoekt u zelf hulp? Ga naar Hulp nodig.",
  });
  const [form, setForm] = useState({
    naam: "",
    email: "",
    telefoon: "",
    organisatie: "",
    bericht: "",
    gevonden_via: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", {
        type: "contact",
        naam: form.naam,
        email: form.email,
        telefoon: form.telefoon || undefined,
        organisatie: form.organisatie || undefined,
        bericht: form.bericht,
        gevonden_via: form.gevonden_via || undefined,
      });
      toast.success("Bedankt voor uw bericht — we nemen zo snel mogelijk contact met u op.");
      setForm({ naam: "", email: "", telefoon: "", organisatie: "", bericht: "", gevonden_via: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative bg-petrol text-cream overflow-hidden">
        <div aria-hidden="true" className="absolute -top-32 -right-24 w-[500px] h-[500px] rounded-full bg-gold/15 blur-3xl" />
        <div className="container-x pt-20 md:pt-24 pb-14 max-w-4xl relative">
          <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-5">Contact</p>
          <h1 className="font-display text-[clamp(2.4rem,6vw,4.6rem)] leading-[1.02] tracking-tight text-balance">
            Kort gesprek. <em className="italic text-gold">Grote impact.</em>
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-cream/85 text-pretty">
            Voor algemene vragen, samenwerking of doorverwijzing. Zoekt u zelf hulp?{" "}
            <Link to="/hulp-nodig" className="underline text-gold hover:text-goldSoft">Ga naar Hulp nodig</Link>.
          </p>
        </div>
      </section>

      <section className="py-20 bg-cream">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-5 space-y-6">
            <div className="bg-sandSoft border border-line rounded-3xl p-7 shadow-soft">
              <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-5">Bereikbaarheid</p>
              <ul className="space-y-5 text-sm text-inkMuted">
                <li className="flex items-start gap-3">
                  <Mail size={18} className="text-gold mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] font-bold text-inkMuted/70 mb-0.5">E-mail</p>
                    <a href="mailto:info@stichting-fin.nl" className="text-petrol hover:text-gold transition font-semibold" data-testid="contact-email">
                      info@stichting-fin.nl
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Phone size={18} className="text-gold mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] font-bold text-inkMuted/70 mb-0.5">Telefoon</p>
                    <a href="tel:+31643828430" className="text-petrol hover:text-gold transition font-semibold" data-testid="contact-phone">
                      +31 6 43 82 84 30
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Clock size={18} className="text-gold mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] font-bold text-inkMuted/70 mb-0.5">Openingstijden</p>
                    <p className="text-petrol">Maandag t/m vrijdag<br />09:00 – 17:00 uur</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-gold mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] font-bold text-inkMuted/70 mb-0.5">Locatie</p>
                    <p className="text-petrol">Rotterdam · landelijk actief<br /><span className="italic opacity-70 text-sm">Bezoekadres op afspraak</span></p>
                  </div>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-line">
                <WhatsAppButton showLabel testId="contact-whatsapp" />
              </div>
            </div>

            <div className="bg-goldSoft border border-line rounded-3xl p-7">
              <div className="flex items-start gap-3">
                <Lock size={18} className="text-petrol mt-0.5 shrink-0" />
                <div>
                  <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-petrol mb-1.5">Privacy</p>
                  <p className="text-sm text-petrol/80 leading-relaxed">
                    Deel via dit formulier geen BSN, dossiers of medische informatie. Zie ook ons <Link to="/privacy" className="underline hover:text-goldDark">privacybeleid</Link>.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-ivory border border-line rounded-3xl p-7">
              <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-3">Officieel</p>
              <p className="text-sm text-inkMuted">Stichting FIN · KvK 89903293</p>
            </div>
          </aside>

          <div className="lg:col-span-7">
            <form onSubmit={onSubmit} className="bg-ivory border border-line rounded-3xl p-6 md:p-8 shadow-soft space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Naam" name="naam" value={form.naam} onChange={onChange} required testId="contact-naam" />
                <Field label="E-mail" type="email" name="email" value={form.email} onChange={onChange} required testId="contact-email-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Telefoon (optioneel)" name="telefoon" value={form.telefoon} onChange={onChange} testId="contact-telefoon" />
                <Field label="Organisatie (optioneel)" name="organisatie" value={form.organisatie} onChange={onChange} testId="contact-organisatie" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-inkMuted block mb-1.5">Bericht *</label>
                <textarea
                  name="bericht"
                  value={form.bericht}
                  onChange={onChange}
                  required
                  rows={6}
                  className="w-full bg-cream border border-line rounded-xl px-4 py-3 text-sm focus:border-gold focus:outline-none transition"
                  placeholder="Waar zoekt u contact over? Vertel gerust in eigen woorden…"
                  data-testid="contact-bericht"
                />
              </div>
              <Field label="Hoe heeft u ons gevonden? (optioneel)" name="gevonden_via" value={form.gevonden_via} onChange={onChange} testId="contact-gevonden-via" />
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-petrol hover:bg-goldSoft disabled:opacity-60 disabled:cursor-not-allowed transition rounded-full px-6 py-3.5 text-sm font-semibold shadow-gold"
                data-testid="contact-submit"
              >
                {loading ? "Versturen…" : "Verstuur bericht"} <ArrowUpRight size={16} />
              </button>
              <p className="text-xs text-inkMuted/70 text-center">
                Uw gegevens worden vertrouwelijk behandeld. We gebruiken ze uitsluitend om contact met u op te nemen.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required = false, testId }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-inkMuted block mb-1.5">
        {label}{required && " *"}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-cream border border-line rounded-xl px-4 py-3 text-sm focus:border-gold focus:outline-none transition"
        data-testid={testId}
      />
    </div>
  );
}
